"use client";

import React, { useState } from "react";
import Button from "@/components/Button";
import DatePickerInput from "@/components/datepicker/DatePickerInput";
import { MIN_APPLICANT_AGE, isOldEnough, latestBirthdate } from "@/lib/age";
import { BIRTHDATE_SAVED, announce } from "@/lib/authEvents";
import { useTranslations } from "@/providers/TranslationsProvider";
const FIELD = "block w-full h-[52px] rounded-full bg-[#F2F2F7] dark:bg-[#2C2C2E] px-6 text-[16px] leading-[52px] text-[var(--foreground)] outline-none focus-visible:ring-2 focus-visible:ring-[#0071E3]";
interface BirthdateFormProps {
  onSaved: (birthdate: string) => void;
  onSignOut: () => void;
  /** The host modal titles itself from this ("Accounts are for adults"). */
  onMinorChange?: (minor: boolean) => void;
}

/**
 * The birth-date question, without its modal: BirthdateGate shows it on its
 * own, SignInModal as the step after a new account's code is verified.
 * Accounts are for adults (terms of use), so the calendar stops 18 years
 * back; an under-18 date the server still rejects tells the visitor a parent
 * or guardian books for minors.
 */
export default function BirthdateForm({
  onSaved,
  onSignOut,
  onMinorChange
}: Readonly<BirthdateFormProps>) {
  const {
    t
  } = useTranslations();
  const [birthdate, setBirthdate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [minor, setMinorState] = useState(false);
  const setMinor = (value: boolean) => {
    setMinorState(value);
    onMinorChange?.(value);
  };
  const save = async () => {
    if (!birthdate) {
      setError(t("birthdate gate.required", "Please choose your date of birth."));
      return;
    }
    if (!isOldEnough(birthdate)) {
      setMinor(true);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await fetch("/api/v1/users/birthdate", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          birthdate
        })
      });
      const data: {
        success?: boolean;
        errors?: string[];
      } = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        onSaved(birthdate);
        announce(BIRTHDATE_SAVED);
      } else if (data.errors?.includes("too_young")) {
        setMinor(true);
      } else {
        setError(t("birthdate gate.failed", "We couldn't save it. Please try again."));
      }
    } catch {
      setError(t("birthdate gate.failed", "We couldn't save it. Please try again."));
    } finally {
      setSaving(false);
    }
  };
  const age = String(MIN_APPLICANT_AGE);
  return (
    // p-3 like the sign-in modal: clears the absolutely placed title on phones.
    <div className="p-3">
      {minor && <p className="text-[14px] sm:text-[15px] leading-[1.47] text-[var(--foreground-secondary)] text-justify hyphens-auto">
          {t("birthdate gate.minor", "You need to be at least {{age}} to use your own account. A parent or legal guardian can book sessions for you from their account.").replace("{{age}}", age)}
        </p>}

      {!minor && <div className="mt-3" dir="ltr">
          <DatePickerInput className={FIELD} value={birthdate} maxDate={latestBirthdate()} onChange={value_0 => {
          setBirthdate(value_0);
          setError("");
        }} />
          {error && <p role="alert" className="mt-2 px-2 text-[13px] text-[#E30000]">
              {error}
            </p>}
        </div>}

      <div className="mt-6 flex flex-col gap-2">
        {minor ? <>
            <Button type="button" variant="apple" size="xl" onClick={onSignOut}>
              {t("auth.logout", "Logout")}
            </Button>
            <Button type="button" variant="apple-gray" size="xl" onClick={() => setMinor(false)}>
              {t("birthdate gate.change", "Change date")}
            </Button>
          </> : <>
            <Button type="button" variant="apple" size="xl" onClick={save} disabled={saving}>
              {t("birthdate gate.save", "Continue")}
            </Button>
            <button type="button" onClick={onSignOut} className="py-2 text-[14px] text-[var(--foreground-secondary)] hover:text-[#0071E3] cursor-pointer">
              {t("auth.logout", "Logout")}
            </button>
          </>}
      </div>
    </div>
  );
}